import React, { useState } from "react";
import { useGlobalContext } from "../../context/global";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography,
  Tooltip,
} from "@mui/material";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

const MarketplaceTooltip = ({ name, children, onRequestIntegration }) => (
  <Tooltip
    placement="right"
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#363636",
          color: "#fff",
          fontSize: "12px",
          borderRadius: "8px",
          padding: 0,
          maxWidth: 320,
          "& .MuiTooltip-arrow": { color: "#363636" },
        },
      },
    }}
    arrow
    title={
      <Box sx={{ p: 1.5, minWidth: 240 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 0.5,
              bgcolor: "#FF8C00",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LockOutlinedIcon sx={{ color: "#fff", fontSize: 14 }} />
          </Box>
          <Typography component="span" fontWeight={600} fontSize="16px">
            {name} Not Connected
          </Typography>
        </Box>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.8)",
            fontSize: "12px",
            lineHeight: 1.5,
            mb: 1.5,
          }}
        >
          Connect {name} to view analytics,
          <br />
          manage listings, and sync inventory
        </Typography>
        <Button
          fullWidth
          variant="contained"
          startIcon={<SendRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={(e) => {
            e.stopPropagation();
            onRequestIntegration?.(name);
          }}
          sx={{
            backgroundColor: "primary.main",
            color: "#fff",
            fontWeight: 600,
            fontSize: "12px",
            textTransform: "none",
            borderRadius: "8px",
            py: 1,
            "&:hover": {
              backgroundColor: "primary.light",
            },
          }}
        >
          Request Integration
        </Button>
      </Box>
    }
  >
    <span style={{ display: "inline-flex", width: "100%" }}>{children}</span>
  </Tooltip>
);

const PlatformTab = ({ platforms = [] }) => {
  const { selectedPlatform, updatePlatform } = useGlobalContext();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const selected = platforms.find(
    (p) => p.name?.toLowerCase() === selectedPlatform
  ) || platforms[0];

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (platform) => {
    if (platform.isDisabled) return;
    updatePlatform(platform.name?.toLowerCase());
    navigate("/");
    handleClose();
    window.location.reload();
  };

  if (!platforms.length) return null;

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
        sx={{
          textTransform: "none",
          borderColor: "#c4c4c4",
          borderRadius: 1,
          px: 1.5,
          py: 0.5,
          height: 36,
          minHeight: 36,
          color: "#333333",
          fontSize: "12px",
          backgroundColor: "#ffffff",
          "&:hover": {
            borderColor: "#c4c4c4",
            backgroundColor: "grey.50",
          },
          "& svg": { color: "#333333" },
        }}
        aria-controls={open ? "marketplace-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: selected?.isDisabled ? "grey.400" : "success.main",
            mr: 1.25,
            flexShrink: 0,
          }}
        />
        {selected?.src && (
          <Box
            component="img"
            src={selected.src}
            alt={selected.name}
            sx={{ height: 20, width: "auto", mr: 1.25 }}
          />
        )}
        <Typography variant="body2" fontWeight={500}>
          {selected?.name ?? "Marketplace"}
        </Typography>
      </Button>
      <Menu
        id="marketplace-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "marketplace-button",
          sx: {
            py: 1,
            paddingTop: 1,
            paddingBottom: 1,
            backgroundColor: "#ffffff",
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            width: 200,
            minWidth: 200,
            maxHeight: 250,
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: "#ffffff",
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 1,
              bgcolor: "divider",
            },
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            px: "12px",
            py: 1.25,
            display: "block",
            color: "#333333",
            fontWeight: 600,
            letterSpacing: "0.08em",
            fontSize: "12px",
          }}
        >
          MARKETPLACE
        </Typography>
        {platforms.map((platform) => {
          const isSelected =
            platform.name?.toLowerCase() === selectedPlatform;
          const isDisabled = platform.isDisabled;
          const item = (
            <ListItemButton
              key={platform.name}
              selected={isSelected}
              disabled={isDisabled}
              onClick={() => handleSelect(platform)}
              sx={{
                minHeight: "36px",
                padding: "6px 12px",
                fontSize: "12px",
                color: "#333333",
                backgroundColor: "#ffffff",
                "&:hover": {
                  backgroundColor: "grey.50",
                },
                "&.Mui-selected": {
                  backgroundColor: "grey.100 !important",
                  color: "#333333 !important",
                  "&:hover": {
                    backgroundColor: "grey.100 !important",
                    color: "#333333 !important",
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {platform.src && (
                  <Box
                    component="img"
                    src={platform.src}
                    alt={platform.name}
                    sx={{ height: 20, width: "auto" }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={platform.name}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "inherit",
                }}
              />
            </ListItemButton>
          );
          return isDisabled ? (
            <MarketplaceTooltip key={platform.name} name={platform.name}>
              {item}
            </MarketplaceTooltip>
          ) : (
            <React.Fragment key={platform.name}>{item}</React.Fragment>
          );
        })}
      </Menu>
    </>
  );
};

export default PlatformTab;
